import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Flight as FlightIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import axios from 'axios';

// ===========================
// Smart Value Renderers
// ===========================

// Format time with timezone (L=Local, Z=UTC)
const formatTimeWithZone = (timeObj, showZone = 'local') => {
  if (!timeObj) return '--:--';
  
  // Handle nested time object
  const utcString = typeof timeObj === 'string' ? timeObj : timeObj.utc;
  const localString = timeObj.local_wall;
  
  if (!utcString) return '--:--';
  
  try {
    if (showZone === 'utc' || showZone === 'zulu') {
      const date = new Date(utcString);
      const time = date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false,
        timeZone: 'UTC'
      });
      return `${time}Z`;
    } else {
      const date = new Date(localString || utcString);
      const time = date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
      return `${time}L`;
    }
  } catch {
    return '--:--';
  }
};

// Format date as "Mon, Jan 5"
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch {
    return 'N/A';
  }
};

// Format duration from minutes
const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return 'N/A';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${mins.toString().padStart(2, '0')}`;
};

// Flight card component (compact version for comparison)
const FlightCard = ({ flight, variant = 'default' }) => {
  const blockMinutes = flight.sched_block?.minutes || 0;
  const blockTime = formatDuration(blockMinutes);
  
  // Determine background based on variant (old vs new)
  const bgColor = variant === 'old' ? 'grey.50' : 'success.50';
  const borderColor = variant === 'old' ? 'grey.300' : 'success.main';

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        bgcolor: bgColor,
        border: '1px solid',
        borderColor: borderColor,
        borderRadius: 1.5
      }}
    >
      {/* Header with flight number and equipment */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <FlightIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        <Typography variant="subtitle1" fontWeight="700">
          {flight.flight_number || 'Flight'}
        </Typography>
        {flight.equipment && (
          <Chip
            label={flight.equipment}
            size="small"
            sx={{ 
              height: 20,
              fontSize: '0.7rem',
              bgcolor: 'primary.main',
              color: 'white',
              fontWeight: 600
            }}
          />
        )}
      </Box>

      {/* Route */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight="700">
            {flight.origin || flight.departure}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Departure
          </Typography>
        </Box>

        <Box sx={{ flex: 1, position: 'relative' }}>
          <Divider sx={{ borderStyle: 'solid', borderWidth: 1 }} />
          <Box 
            sx={{ 
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'primary.main',
              borderRadius: '50%',
              width: 8,
              height: 8
            }}
          />
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ 
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: '50%',
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap'
            }}
          >
            {blockTime}
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight="700">
            {flight.destination || flight.arrival}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Arrival
          </Typography>
        </Box>
      </Box>

      {/* Times */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Departs
          </Typography>
          <Typography variant="body2" fontWeight="600" fontFamily="monospace">
            {formatTimeWithZone(flight.sched_out || flight.scheduled_departure)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Arrives
          </Typography>
          <Typography variant="body2" fontWeight="600" fontFamily="monospace">
            {formatTimeWithZone(flight.sched_in || flight.scheduled_arrival)}
          </Typography>
        </Box>
      </Box>

      {/* Deadhead badge */}
      {flight.is_deadhead && (
        <Chip
          label="🚶 DEADHEAD"
          size="small"
          sx={{ 
            mt: 1.5,
            width: '100%',
            bgcolor: 'warning.light',
            color: 'warning.dark',
            fontWeight: 600
          }}
        />
      )}
    </Paper>
  );
};

// Smart renderer - detects data type and renders appropriately
const renderSmartValue = (value, field) => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        Not present
      </Typography>
    );
  }

  // Detect flight object (has flight_number + origin + destination)
  if (
    typeof value === 'object' && 
    value !== null && 
    (value.flight_number || value.departure || value.origin) &&
    (value.destination || value.arrival)
  ) {
    return <FlightCard flight={value} variant={field.includes('old') ? 'old' : 'new'} />;
  }

  // Detect time object (has utc or local_wall)
  if (
    typeof value === 'object' && 
    value !== null && 
    (value.utc || value.local_wall)
  ) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScheduleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="body1" fontWeight="600" fontFamily="monospace">
          {formatTimeWithZone(value)}
        </Typography>
      </Box>
    );
  }

  // Detect date string (YYYY-MM-DD format)
  if (
    typeof value === 'string' && 
    /^\d{4}-\d{2}-\d{2}/.test(value) &&
    (field.includes('date') || field.includes('Date'))
  ) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="body1" fontWeight="600">
          {formatDate(value)}
        </Typography>
      </Box>
    );
  }

  // Detect duration/minutes
  if (
    typeof value === 'number' && 
    (field.includes('minutes') || field.includes('time') || field.includes('duration'))
  ) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScheduleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="body1" fontWeight="600" fontFamily="monospace">
          {formatDuration(value)}
        </Typography>
      </Box>
    );
  }

  // Detect array of flights
  if (Array.isArray(value) && value.length > 0 && value[0]?.flight_number) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {value.map((flight, idx) => (
          <FlightCard key={idx} flight={flight} variant={field.includes('old') ? 'old' : 'new'} />
        ))}
      </Box>
    );
  }

  // For other objects, show a compact summary
  if (typeof value === 'object' && value !== null) {
    // Try to extract meaningful fields
    const keys = Object.keys(value);
    if (keys.length <= 3) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {keys.map((key) => (
            <Typography key={key} variant="body2">
              <strong>{key}:</strong> {String(value[key])}
            </Typography>
          ))}
        </Box>
      );
    }
    // For complex objects, show field count
    return (
      <Typography variant="body2" color="text.secondary">
        Object with {keys.length} fields
      </Typography>
    );
  }

  // Simple values (string, number, boolean)
  return (
    <Typography variant="body1" fontWeight="600">
      {String(value)}
    </Typography>
  );
};

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API_BASE = `${API_URL}/api/v1`;

const ComparePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [version1, setVersion1] = useState(null);
  const [version2, setVersion2] = useState(null);
  const [differences, setDifferences] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  const entityType = searchParams.get('type');
  const version1Slug = searchParams.get('v1');
  const version2Slug = searchParams.get('v2');

  // Parse slug to extract rotation_number and version
  const parseSlug = (slug) => {
    if (!slug) return [null, null];
    if (!slug.includes('-')) return [slug, null];
    const [number, version] = slug.split('-', 2);
    return [number, version];
  };

  // Fetch both versions
  useEffect(() => {
    const fetchVersions = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('=== COMPARE PAGE DEBUG ===');
        console.log('Entity Type:', entityType);
        console.log('Version 1 Slug from URL:', version1Slug);
        console.log('Version 2 Slug from URL:', version2Slug);

        const [number1, ver1] = parseSlug(version1Slug);
        const [number2, ver2] = parseSlug(version2Slug);

        console.log('Parsed Version 1:', { number: number1, version: ver1 });
        console.log('Parsed Version 2:', { number: number2, version: ver2 });

        if (!number1 || !number2) {
          console.error('❌ Invalid version identifiers');
          setError('Invalid version identifiers');
          setLoading(false);
          return;
        }

        // Construct API URLs based on entity type
        const baseUrl = entityType === 'rotation' 
          ? `${API_BASE}/rotations`
          : `${API_BASE}/timecards`;

        console.log('API Base URL:', baseUrl);

        const url1 = `${baseUrl}/${version1Slug}/compiled`;
        const url2 = `${baseUrl}/${version2Slug}/compiled`;

        console.log('Requesting URL 1:', url1);
        console.log('Requesting URL 2:', url2);

        // Fetch both versions in parallel
        const [response1, response2] = await Promise.all([
          axios.get(url1),
          axios.get(url2),
        ]);

        console.log('✅ Response 1 Status:', response1.status);
        console.log('✅ Response 2 Status:', response2.status);
        console.log('Response 1 Data Preview:', {
          rotation_number: response1.data.rotation_number,
          employee_id: response1.data.employee_id,
          total_duty_minutes: response1.data.total_duty_minutes
        });
        console.log('Response 2 Data Preview:', {
          rotation_number: response2.data.rotation_number,
          employee_id: response2.data.employee_id,
          total_duty_minutes: response2.data.total_duty_minutes
        });

        setVersion1(response1.data);
        setVersion2(response2.data);

        // Calculate differences
        const diffs = calculateDifferences(response1.data, response2.data);
        console.log('📊 Differences Calculated:', diffs.length, 'changes');
        setDifferences(diffs);
      } catch (err) {
        console.error('❌ ERROR FETCHING VERSIONS:', err);
        console.error('Error Details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          config: {
            url: err.config?.url,
            method: err.config?.method,
            baseURL: err.config?.baseURL
          }
        });
        setError(err.response?.data?.message || err.response?.data?.detail || 'Failed to load versions');
      } finally {
        setLoading(false);
      }
    };

    if (version1Slug && version2Slug) {
      fetchVersions();
    }
  }, [version1Slug, version2Slug, entityType]);

  // Calculate differences between two versions
  const calculateDifferences = (v1, v2) => {
    const diffs = [];

    if (entityType === 'rotation') {
      // ROTATION COMPARISON
      // Compare identity section
      if (v1.compiled_json?.meta && v2.compiled_json?.meta) {
        diffs.push(...compareSections(v1.compiled_json.meta, v2.compiled_json.meta, 'Identity'));
      }

      // Compare schedule section
      if (v1.compiled_json?.meta && v2.compiled_json?.meta) {
        const schedule1 = {
          rotation_start_date: v1.compiled_json.meta.rotation_start_date,
          rotation_end_date: v1.compiled_json.meta.rotation_end_date,
        };
        const schedule2 = {
          rotation_start_date: v2.compiled_json.meta.rotation_start_date,
          rotation_end_date: v2.compiled_json.meta.rotation_end_date,
        };
        diffs.push(...compareSections(schedule1, schedule2, 'Schedule'));
      }

      // Compare flights
      if (v1.compiled_json?.duty_periods && v2.compiled_json?.duty_periods) {
        diffs.push(...compareFlights(v1.compiled_json.duty_periods, v2.compiled_json.duty_periods));
      }

      // Compare totals
      const totals1 = {
        total_sched_block_minutes: v1.compiled_json?.total_sched_block_minutes,
        total_duty_minutes: v1.compiled_json?.total_duty_minutes,
      };
      const totals2 = {
        total_sched_block_minutes: v2.compiled_json?.total_sched_block_minutes,
        total_duty_minutes: v2.compiled_json?.total_duty_minutes,
      };
      diffs.push(...compareSections(totals1, totals2, 'Totals'));

    } else if (entityType === 'timecard') {
      // TIMECARD COMPARISON
      // Compare credit breakdown
      if (v1.compiled_json?.credit_breakdown && v2.compiled_json?.credit_breakdown) {
        diffs.push(...compareSections(
          v1.compiled_json.credit_breakdown,
          v2.compiled_json.credit_breakdown,
          'Credit Hours'
        ));
      }

      // Compare guarantee analysis
      if (v1.compiled_json?.guarantee_analysis && v2.compiled_json?.guarantee_analysis) {
        diffs.push(...compareSections(
          v1.compiled_json.guarantee_analysis,
          v2.compiled_json.guarantee_analysis,
          'Guarantee'
        ));
      }

      // Compare per diem breakdown
      if (v1.compiled_json?.per_diem_breakdown && v2.compiled_json?.per_diem_breakdown) {
        diffs.push(...compareSections(
          v1.compiled_json.per_diem_breakdown,
          v2.compiled_json.per_diem_breakdown,
          'Per Diem'
        ));
      }

      // Compare rotation entries
      if (v1.compiled_json?.rotations && v2.compiled_json?.rotations) {
        diffs.push(...compareTimecardRotations(
          v1.compiled_json.rotations,
          v2.compiled_json.rotations
        ));
      }
    }

    return diffs;
  };

  // Compare two objects field by field
  const compareSections = (obj1, obj2, sectionName) => {
    // Fields to exclude from comparison (technical/internal fields)
    const EXCLUDED_FIELDS = [
      'rotation_id', 'trace_id', 'compiled_at', 'source_version',
      'version_letter', 'id', 'created_at', 'updated_at', 
      'parsed_rotation_id', 'compiler_version', 'parse_timestamp',
      'upload_timestamp', 'timecard_id', 'source_data_hash'
    ];

    // User-friendly field labels
    const FIELD_LABELS = {
      'total_duty_minutes': 'Total Duty Time',
      'total_block_minutes': 'Total Block Time',
      'total_credit_minutes': 'Total Credit Time',
      'total_sched_block_minutes': 'Total Scheduled Block',
      'total_actual_block_minutes': 'Total Actual Block',
      'total_deadhead_minutes': 'Total Deadhead',
      'employee_id': 'Employee ID',
      'employee_name': 'Employee Name',
      'rotation_number': 'Rotation Number',
      'rotation_type': 'Rotation Type',
      'assignment_type': 'Assignment Type',
      'rotation_start_date': 'Start Date',
      'rotation_end_date': 'End Date',
      'rotation_start_time': 'Start Time',
      'rotation_end_time': 'End Time',
      'duration_days': 'Duration (Days)',
      'bid_period': 'Bid Period',
      'base': 'Base',
      'equipment': 'Equipment',
      'position': 'Position',
      'flight_number': 'Flight Number',
      'departure_airport': 'Departure',
      'arrival_airport': 'Arrival',
      'aircraft_type': 'Aircraft',
      'far117_compliant': 'FAR 117 Compliant',
    };

    // Format field name
    const formatFieldName = (field) => {
      if (FIELD_LABELS[field]) return FIELD_LABELS[field];
      return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // Format value for display
    const formatValue = (key, value) => {
      if (value === null || value === undefined) return 'Not set';
      if (value === '') return 'Empty';
      
      // Format minutes as hours and minutes
      if (key.includes('_minutes') && typeof value === 'number') {
        const hours = Math.floor(value / 60);
        const mins = value % 60;
        return `${hours}h ${mins}m`;
      }
      
      // Format booleans
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      
      // Format arrays
      if (Array.isArray(value)) {
        if (value.length === 0) return 'None';
        return `${value.length} items`;
      }
      
      // Format objects (show summary, not full JSON)
      if (typeof value === 'object') {
        return '[Complex data]';
      }
      
      return String(value);
    };

    const diffs = [];
    const allKeys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

    allKeys.forEach((key) => {
      // Skip excluded fields
      if (EXCLUDED_FIELDS.includes(key)) return;

      const val1 = obj1?.[key];
      const val2 = obj2?.[key];

      if (JSON.stringify(val1) !== JSON.stringify(val2)) {
        let changeType = 'modified';
        if (val1 === undefined || val1 === null) changeType = 'added';
        if (val2 === undefined || val2 === null) changeType = 'removed';

        diffs.push({
          section: sectionName,
          field: key,
          fieldLabel: formatFieldName(key),
          oldValue: val1,
          newValue: val2,
          oldValueFormatted: formatValue(key, val1),
          newValueFormatted: formatValue(key, val2),
          changeType,
        });
      }
    });

    return diffs;
  };

  // Compare timecard rotation entries
  const compareTimecardRotations = (rotations1, rotations2) => {
    const diffs = [];
    
    // Create rotation identifiers
    const getRotationId = (rot) => `${rot.rotation_number}_${rot.date}`;

    const rotations1Map = new Map((rotations1 || []).map(r => [getRotationId(r), r]));
    const rotations2Map = new Map((rotations2 || []).map(r => [getRotationId(r), r]));

    // Find added rotations
    rotations2Map.forEach((rotation, id) => {
      if (!rotations1Map.has(id)) {
        diffs.push({
          section: 'Rotations',
          field: `Rotation ${rotation.rotation_number}`,
          oldValue: null,
          newValue: rotation,
          changeType: 'added',
        });
      }
    });

    // Find removed rotations
    rotations1Map.forEach((rotation, id) => {
      if (!rotations2Map.has(id)) {
        diffs.push({
          section: 'Rotations',
          field: `Rotation ${rotation.rotation_number}`,
          oldValue: rotation,
          newValue: null,
          changeType: 'removed',
        });
      }
    });

    // Find modified rotations
    rotations1Map.forEach((rotation1, id) => {
      const rotation2 = rotations2Map.get(id);
      if (rotation2) {
        // Check for credit hour changes
        if (rotation1.credit_hours !== rotation2.credit_hours) {
          diffs.push({
            section: 'Rotations',
            field: `Rotation ${rotation1.rotation_number} Credit Hours`,
            oldValue: rotation1.credit_hours,
            newValue: rotation2.credit_hours,
            changeType: 'modified',
          });
        }
      }
    });

    return diffs;
  };

  // Compare flight arrays
  const compareFlights = (dutyPeriods1, dutyPeriods2) => {
    const diffs = [];
    
    // Extract all flights from duty periods
    const flights1 = dutyPeriods1.flatMap(dp => dp.legs || []);
    const flights2 = dutyPeriods2.flatMap(dp => dp.legs || []);

    // Create flight identifiers
    const getFlightId = (flight) => `${flight.flight_number}_${flight.departure}_${flight.arrival}`;

    const flights1Map = new Map(flights1.map(f => [getFlightId(f), f]));
    const flights2Map = new Map(flights2.map(f => [getFlightId(f), f]));

    // Find added flights
    flights2Map.forEach((flight, id) => {
      if (!flights1Map.has(id)) {
        diffs.push({
          section: 'Flights',
          field: `Flight ${flight.flight_number || 'Unknown'}`,
          oldValue: null,
          newValue: flight,
          changeType: 'added',
        });
      }
    });

    // Find removed flights
    flights1Map.forEach((flight, id) => {
      if (!flights2Map.has(id)) {
        diffs.push({
          section: 'Flights',
          field: `Flight ${flight.flight_number || 'Unknown'}`,
          oldValue: flight,
          newValue: null,
          changeType: 'removed',
        });
      }
    });

    // Find modified flights
    flights1Map.forEach((flight1, id) => {
      const flight2 = flights2Map.get(id);
      if (flight2) {
        // Check for changes in times or other fields
        if (JSON.stringify(flight1) !== JSON.stringify(flight2)) {
          diffs.push({
            section: 'Flights',
            field: `Flight ${flight1.flight_number || 'Unknown'}`,
            oldValue: flight1,
            newValue: flight2,
            changeType: 'modified',
          });
        }
      }
    });

    return diffs;
  };

  // Get change icon
  const getChangeIcon = (changeType) => {
    switch (changeType) {
      case 'added':
        return <AddIcon fontSize="small" />;
      case 'removed':
        return <RemoveIcon fontSize="small" />;
      case 'modified':
        return <EditIcon fontSize="small" />;
      default:
        return <CheckCircleIcon fontSize="small" />;
    }
  };

  // Get change color
  const getChangeColor = (changeType) => {
    switch (changeType) {
      case 'added':
        return 'success';
      case 'removed':
        return 'error';
      case 'modified':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Filter differences by tab
  const filteredDifferences = differences.filter((diff) => {
    if (tabValue === 0) return true; // All Changes
    
    if (entityType === 'rotation') {
      // Rotation tabs
      if (tabValue === 1) return diff.section === 'Identity';
      if (tabValue === 2) return diff.section === 'Schedule';
      if (tabValue === 3) return diff.section === 'Flights';
      if (tabValue === 4) return diff.section === 'Totals';
    } else if (entityType === 'timecard') {
      // Timecard tabs
      if (tabValue === 1) return diff.section === 'Credit Hours';
      if (tabValue === 2) return diff.section === 'Guarantee';
      if (tabValue === 3) return diff.section === 'Per Diem';
      if (tabValue === 4) return diff.section === 'Rotations';
    }
    
    return false;
  });

  // Count changes by type
  const changeCounts = {
    added: differences.filter((d) => d.changeType === 'added').length,
    removed: differences.filter((d) => d.changeType === 'removed').length,
    modified: differences.filter((d) => d.changeType === 'modified').length,
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!version1 || !version2) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">No versions found to compare</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/data-management')}
            sx={{ cursor: 'pointer' }}
          >
            Data Management
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/audit?type=${entityType}&number=${parseSlug(version1Slug)[0]}`)}
            sx={{ cursor: 'pointer' }}
          >
            Audit
          </Link>
          <Typography color="text.primary">Compare Versions</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Version Comparison
        </Typography>
      </Box>

      {/* Instruction Text */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>📋 How to compare versions:</strong> Select two <strong>different</strong> versions from the dropdowns below to see changes.
          {' '}For example, compare <strong>v1 (schedule)</strong> vs <strong>v2 (reroute)</strong> to see what changed.
        </Typography>
      </Alert>

      {/* Version Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Version 1
              </Typography>
              {entityType === 'rotation' ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Rotation: {version1.compiled_json?.meta?.rotation_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Date: {version1.compiled_json?.meta?.rotation_start_date}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Employee: {version1.employee_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bid Period: {version1.bid_period}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Credit Hours: {version1.compiled_json?.credit_breakdown?.total_credit_hours?.toFixed(2) || 'N/A'}
                  </Typography>
                </>
              )}
              <Chip
                label={version1Slug}
                size="small"
                color="primary"
                sx={{ mt: 2, fontWeight: 600 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              border: '2px solid',
              borderColor: 'secondary.main',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                transform: 'translateY(-2px)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Version 2
              </Typography>
              {entityType === 'rotation' ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Rotation: {version2.compiled_json?.meta?.rotation_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Date: {version2.compiled_json?.meta?.rotation_start_date}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Employee: {version2.employee_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bid Period: {version2.bid_period}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Credit Hours: {version2.compiled_json?.credit_breakdown?.total_credit_hours?.toFixed(2) || 'N/A'}
                  </Typography>
                </>
              )}
              <Chip
                label={version2Slug}
                size="small"
                color="secondary"
                sx={{ mt: 2, fontWeight: 600 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Change Summary */}
      {differences.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{differences.length} changes detected:</strong>{' '}
            {changeCounts.added > 0 && `${changeCounts.added} added, `}
            {changeCounts.removed > 0 && `${changeCounts.removed} removed, `}
            {changeCounts.modified > 0 && `${changeCounts.modified} modified`}
          </Typography>
        </Alert>
      )}

      {/* Empty State */}
      {differences.length === 0 && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>No differences found</strong> - These versions are identical
          </Typography>
        </Alert>
      )}

      {/* Tabs */}
      {differences.length > 0 && (
        <>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
            <Tab label={`All Changes (${differences.length})`} />
            {entityType === 'rotation' ? (
              <>
                <Tab label="Identity" />
                <Tab label="Schedule" />
                <Tab label="Flights" />
                <Tab label="Totals" />
              </>
            ) : (
              <>
                <Tab label="Credit Hours" />
                <Tab label="Guarantee" />
                <Tab label="Per Diem" />
                <Tab label="Rotations" />
              </>
            )}
          </Tabs>

          {/* Diff Cards */}
          <Grid container spacing={2}>
            {filteredDifferences.map((diff, index) => (
              <Grid item xs={12} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 
                      diff.changeType === 'added'
                        ? 'success.main'
                        : diff.changeType === 'removed'
                        ? 'error.main'
                        : 'warning.main',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Chip
                        icon={getChangeIcon(diff.changeType)}
                        label={diff.changeType.toUpperCase()}
                        color={getChangeColor(diff.changeType)}
                        size="small"
                        sx={{ 
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                      <Typography variant="h6" fontWeight="600" color="text.primary">
                        {diff.fieldLabel || diff.field}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ mb: 2 }} />
                    
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <Paper 
                          elevation={0}
                          sx={{ 
                            p: 2,
                            bgcolor: 'grey.50',
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'grey.200',
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            fontWeight="600"
                            textTransform="uppercase"
                            letterSpacing={0.5}
                            sx={{ mb: 1, display: 'block' }}
                          >
                            Previous
                          </Typography>
                          <Box>
                            {diff.oldValueFormatted || renderSmartValue(diff.oldValue, diff.field)}
                          </Box>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} md={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <ArrowForwardIcon 
                          sx={{ 
                            color: 'primary.main',
                            fontSize: 32,
                            display: { xs: 'none', md: 'block' },
                          }} 
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={5}>
                        <Paper 
                          elevation={0}
                          sx={{ 
                            p: 2,
                            bgcolor: diff.changeType === 'added' ? 'success.50' : 'primary.50',
                            borderRadius: 1.5,
                            border: '2px solid',
                            borderColor: diff.changeType === 'added' ? 'success.main' : 'primary.main',
                          }}
                        >
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            fontWeight="600"
                            textTransform="uppercase"
                            letterSpacing={0.5}
                            sx={{ mb: 1, display: 'block' }}
                          >
                            Current
                          </Typography>
                          <Box>
                            {diff.newValueFormatted || renderSmartValue(diff.newValue, diff.field)}
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Container>
  );
};

export default ComparePage;
